import { toast } from "react-toastify";
import { all, put, takeLatest, delay } from "redux-saga/effects";

// Api
import {
  downloadExcelReportFn,
  downloadCampaignPdfReportFn,
  getCampaignSummaryData,
  initiateMavinReportGeneration,
  generateCampaignExcelReportFn,
} from "../../apis/CampaignReportAPI";

// Constants
import { CampaignReport } from "../../constants/action-constants/CampaignReportActionConstants";
import {
  CampaignDemographicStatsStatus,
  CampaignReportStatus,
  ExcelReportStatus,
  FilenameExtensions,
  ReportConstants,
} from "../../constants/GeneralConstants";

// Utils
import { getErrorMessage } from "../../utils/util";
import { saveFile } from "../../common-utils/file-utils/FileUtils";
import { setErrorStatusCode } from "../../utils/ErrorUtils";

/**
 * Campaign overview
 * overview of a particular campaign
 * needs campaign id
 */
export function* getCampaignOverview(action) {
  try {
    const { id } = action.payload;
    const data = yield getCampaignSummaryData(id);

    // dispatching action
    yield put({
      type: CampaignReport.CAMPAIGN_OVERVIEW_SUCCESS,
      data: data,
    });
  } catch (err) {
    // stores the error and render the error image when the api fails
    setErrorStatusCode(err);

    /* istanbul ignore next */
    yield put({
      type: CampaignReport.CAMPAIGN_OVERVIEW_FAILURE,
      payload: err,
    });
  }
}

export function* generateReport(action) {
  try {
    const { campaignId } = action.payload;

    // Initiate the process for generating the report..
    yield initiateMavinReportGeneration(campaignId);

    // Fetch the summary to check if the report is generated
    let data = yield getCampaignSummaryData(campaignId);

    // We pool till the report is generated..
    while (data.summary.reportStatus !== CampaignReportStatus.GENERATED) {
      data = yield getCampaignSummaryData(campaignId);
      yield delay(5000);
    }

    yield put({
      type: CampaignReport.GENERATE_MAVIN_REPORT_SUCCESS,
    });

    // Once the report is generated we update the campaign summary details to reflect in the UI
    yield put({
      type: CampaignReport.CAMPAIGN_OVERVIEW_SUCCESS,
      data: data,
    });
  } catch (err) {
    yield put({
      type: CampaignReport.GENERATE_MAVIN_REPORT_FAILURE,
      payload: err,
    });
  }
}

export function* downloadExcelReport(action) {
  try {
    const { campaignId, campaignTitle, isMantaraySpecific } = action.payload;

    const data = yield downloadExcelReportFn(campaignId, isMantaraySpecific);

    // file name
    const excelFileName = campaignTitle
      ? `${campaignTitle}${FilenameExtensions.xlsx}`
      : ReportConstants.CAMPAIGN_XLS_REPORT;

    // save file
    saveFile(excelFileName, data);

    yield put({
      type: CampaignReport.DOWNLOAD_XLS_REPORT_SUCCESS,
      data: data,
    });
  } catch (err) {
    const errMsg = getErrorMessage(err);
    yield put({
      type: CampaignReport.DOWNLOAD_XLS_REPORT_FAILURE,
      payload: err,
    });
    toast.error(errMsg);
  }
}

export function* downloadCampaignPdfReport(action) {
  try {
    const { campaignId, isSentViaEmail, emailIds = [] } = action.payload;

    const data = yield downloadCampaignPdfReportFn(
      campaignId,
      isSentViaEmail,
      emailIds
    );

    yield put({
      type: CampaignReport.DOWNLOAD_CAMPAIGN_PDF_REPORT_SUCCESS,
      payload: { isSentViaEmail, reportData: data },
    });
  } catch (err) {
    const errMsg = getErrorMessage(err);
    yield put({
      type: CampaignReport.DOWNLOAD_CAMPAIGN_PDF_REPORT_FAILURE,
      payload: err,
    });
    toast.error(errMsg);
  }
}

function* pollCampaignSummaryForExcelReport(campaignId) {
  const data = yield getCampaignSummaryData(campaignId);
  const { campaign = {} } = data || {};
  const { excelReportStatus } = campaign || {};

  // constants
  const { GENERATED, FAILED } = ExcelReportStatus;

  // status
  const isExcelReportGenerated = excelReportStatus === GENERATED;
  const isExcelReportFailed = excelReportStatus === FAILED;

  // if failed, return data
  if (isExcelReportFailed) {
    return data;
  }

  // if generated, return data
  if (isExcelReportGenerated) {
    toast.success("Excel report data is generated and can be downloaded.");
    return data;
  }

  // delay in api call every time : 15 seconds
  yield delay(15000);

  // recall the same function to generate excel report
  return yield pollCampaignSummaryForExcelReport(campaignId);
}

function* pollCampaignSummaryForDemographicStats(campaignId) {
  const data = yield getCampaignSummaryData(campaignId);

  const { campaign = {} } = data || {};
  const { demographicStatisticsStatus } = campaign || {};
  const isDemographicStatsInProgress =
    demographicStatisticsStatus === CampaignDemographicStatsStatus.IN_PROGRESS;

  // if demographic stats is not "InProgress", return data
  if (!isDemographicStatsInProgress) {
    yield put({
      type: CampaignReport.CAMPAIGN_OVERVIEW_SUCCESS,
      data: data,
    });
    return;
  }

  // delay in api call every time : 5 seconds
  yield delay(5000);

  // recall campaign summary
  return yield pollCampaignSummaryForDemographicStats(campaignId);
}

export function* generateCampaignExcelReport(action) {
  try {
    const { campaignId, isDemographicStatsInProgress } = action.payload;

    // if "isDemographicStatsInProgress = true",
    // poll campaignSummaryApi until demographicStats are "Processed"
    if (isDemographicStatsInProgress) {
      yield pollCampaignSummaryForDemographicStats(campaignId);
      return;
    }

    // generate campaign excel report api
    yield generateCampaignExcelReportFn(campaignId);

    // calling campaign summary api, till the "excelReportStatus" = "GENERATED"
    const data = yield pollCampaignSummaryForExcelReport(campaignId);

    // generate excel report success
    yield put({
      type: CampaignReport.GENERATE_CAMPAIGN_EXCEL_REPORT_SUCCESS,
    });

    // campaign overview success
    yield put({
      type: CampaignReport.CAMPAIGN_OVERVIEW_SUCCESS,
      data: data,
    });
  } catch (err) {
    yield put({
      type: CampaignReport.GENERATE_CAMPAIGN_EXCEL_REPORT_FAILURE,
      payload: err,
    });
  }
}

export default function* root() {
  yield all([
    takeLatest(CampaignReport.CAMPAIGN_OVERVIEW, getCampaignOverview),
    takeLatest(CampaignReport.GENERATE_MAVIN_REPORT, generateReport),
    takeLatest(CampaignReport.DOWNLOAD_XLS_REPORT, downloadExcelReport),
    takeLatest(
      CampaignReport.DOWNLOAD_CAMPAIGN_PDF_REPORT,
      downloadCampaignPdfReport
    ),
    takeLatest(
      CampaignReport.GENERATE_CAMPAIGN_EXCEL_REPORT,
      generateCampaignExcelReport
    ),
  ]);
}
